/**
 * Utilidades para manejo de moneda
 */

/**
 * Convierte centavos a formato de moneda con signo de pesos y dos decimales
 * @param cents - Valor en centavos
 * @returns String formateado como moneda (ej: "$12.50")
 */
export const formatCentsToCurrency = (
  cents: number | null | undefined,
): string => {
  if (cents === null || cents === undefined) {
    return '-';
  }

  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
};

/**
 * Convierte un valor en dólares a centavos
 * @param dollars - Valor en dólares
 * @returns Número en centavos
 */
export const convertDollarsToCents = (dollars: number | string): number => {
  const numericValue =
    typeof dollars === 'string' ? parseFloat(dollars) : dollars;
  if (isNaN(numericValue)) {
    return 0;
  }
  return Math.round(numericValue * 100);
};

/**
 * Convierte centavos a dólares
 * @param cents - Valor en centavos
 * @returns Número en dólares
 */
export const convertCentsToDollars = (cents: number): number => {
  return cents / 100;
};

/**
 * Formatea un valor en dólares para mostrar en inputs
 * @param cents - Valor en centavos
 * @returns String formateado con dos decimales
 */
export const formatCentsForInput = (
  cents: number | null | undefined,
): string => {
  if (cents === null || cents === undefined) {
    return '';
  }

  return (cents / 100).toFixed(2);
};

/**
 * Valida y limpia input de moneda
 * @param value - Valor del input
 * @returns Valor limpio y válido
 */
export const cleanCurrencyInput = (value: string): string => {
  if (!value) return '';

  // Remove non-numeric characters except decimal point
  let cleanValue = value.replace(/[^0-9.]/g, '');

  // Avoid multiple decimal points
  const parts = cleanValue.split('.');
  if (parts.length > 2) {
    cleanValue = parts[0] + '.' + parts.slice(1).join('');
  }

  // Limit to maximum 2 decimal places
  if (parts.length === 2 && parts[1].length > 2) {
    cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
  }

  // Evitar que empiece con punto
  if (cleanValue.startsWith('.')) {
    cleanValue = '0' + cleanValue;
  }

  return cleanValue;
};

/**
 * Convierte input de moneda a centavos
 * @param value - Valor del input
 * @returns Valor en centavos o undefined si está vacío
 */
export const parseCurrencyInputToCents = (
  value: string,
): number | undefined => {
  if (!value || value.trim() === '') {
    return undefined;
  }

  const cleanValue = cleanCurrencyInput(value);

  // If empty after cleaning, return undefined
  if (!cleanValue) {
    return undefined;
  }

  const dollars = parseFloat(cleanValue);

  if (isNaN(dollars)) {
    return undefined;
  }

  return Math.round(dollars * 100);
};
